import { ClockIcon, DocumentTextIcon, EyeIcon, PencilIcon, QuestionMarkCircleIcon, TagIcon, ThumbDownIcon, ThumbUpIcon, ViewGridIcon } from "@heroicons/react/outline";
import { useOne, useParse } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { DescriptionItem } from "components/descriptionItem";
import { FullScreenLoading } from "components/fullScreenLoading";
import { HistoryLodModal } from "components/historyLogModal";
import { ISupportQADetail, SupportedLanguage } from "interfaces";
import { get } from "lodash";
import { useState } from "react";
import { Link } from "react-router-dom";
import { CONTENT_LANGUAGES } from "utills/constants";
import { formatNumber, isHaveActionPermission } from "utills/helpers";

export const SupportQADetail: React.FC = () => {
  const [selectedContentLanguage, setSelectedContentLanguage] = useState(SupportedLanguage.EN);
  const [openLogModal, setOpenLogModal] = useState(false);

    const parse = useParse();
    const { params } = parse();
    const {
      data,
      isFetching,
      isPreviousData,
      isLoading,
    } = useOne<ISupportQADetail>({
      resource: "cs/support-center/qas",
      id: params?.id,
      meta: {
        resource: "data",
      },
      queryOptions: {
        enabled: !!params?.id,
      } as any,
    });
  
    const supportQADetailData = data?.data ?? null;
  
    if (isLoading) {
      return <FullScreenLoading />
    }
  
    return (
      <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
        <Breadcrumbs
          customPageLabel="Support Center Q&A Detail"
          additionalLabel={
            <div className="flex gap-x-2">
              <Button
                className="bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200"
                onClick={() => setOpenLogModal(true)}
              >
                <span className="sr-only">History Log</span>
                <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
                <span className="hidden sm:block sm:ml-2">Log</span>
              </Button>
              {isHaveActionPermission("support_qa", "cs", "edit") ? (
                <Link to={`/cs/support-qa/${supportQADetailData?.id}/edit`}>
                  <Button>
                    <span className="sr-only">Edit</span>
                    <PencilIcon className="w-4 h-4 text-white" />{" "}
                    <span className="hidden sm:block sm:ml-2">Edit</span>
                  </Button>
                </Link>
              ) : (
                <></>
              )}
            </div>
          }
        />

        <div className="box-container p-0">
          <div className="divide-y divide-gray-100">
            <DescriptionItem
              icon={TagIcon}
              label="Status"
              content={
                <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                  {supportQADetailData?.is_active ? "Active" : "Inactive"}
                </p>
              }
            />
            <DescriptionItem
              icon={QuestionMarkCircleIcon}
              label="Q&A title vie"
              content={
                <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                  {supportQADetailData?.title_vi}
                </p>
              }
            />
            <DescriptionItem
              icon={ViewGridIcon}
              label="Category vie"
              content={
                <Link to={`/cs/support-category/${supportQADetailData?.support_center_category_id}`} className="block w-fit">
                  <p className="w-fit flex gap-x-2 text-sm leading-6 sm:col-span-4 text-brand-500">
                    {supportQADetailData?.support_center_category_name}
                  </p>
                </Link>
              }
            />
            <DescriptionItem
              icon={EyeIcon}
              label="Total View"
              content={
                <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                  {formatNumber(supportQADetailData?.total_views ?? 0)}
                </p>
              }
            />
            <DescriptionItem
              icon={ThumbUpIcon}
              label="Like"
              content={
                <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                  {formatNumber(supportQADetailData?.total_likes ?? 0)}
                </p>
              }
            />
            <DescriptionItem
              icon={ThumbDownIcon}
              label="Dislikes"
              content={
                <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                  {formatNumber(supportQADetailData?.total_dislikes ?? 0)}
                </p>
              }
            />
            <DescriptionItem
              icon={DocumentTextIcon}
              label="Content"
              content={
                <div>
                  <div className="flex flex-row gap-x-1.5 p-1.5 w-fit rounded-full outline outline-solid outline-[1px] outline-brand-500">
                    {CONTENT_LANGUAGES.map((lang, index) => (
                      <img
                        key={lang.value}
                        src={`/images/flags/circle-${lang.value}.png`}
                        alt="Language Icon"
                        className={`w-4 shrink-0 cursor-pointer ${lang.value !== selectedContentLanguage ? 'opacity-20' : ''}`}
                        onClick={() => setSelectedContentLanguage(lang.value)}
                      />
                    ))}
                  </div>
                  <div
                    className="mt-4 reset-tw"
                    dangerouslySetInnerHTML={{
                      __html: `${get(
                        supportQADetailData,
                          `detail_${selectedContentLanguage}`,
                        ""
                      )}`,
                    }}
                  ></div>
                </div>
              }
            />
          </div>
        </div>
        <HistoryLodModal
          resource={`cs/support-center/qas/${params?.id}/history`}
          open={openLogModal}
          onClose={() => setOpenLogModal(false)}
          key="support-category"
          id="support-category"
        />
        {(isFetching && isPreviousData) ? <FullScreenLoading /> : <></>}
      </div>
    );
  }